import type { CSSProperties, ElementType, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

type Direction = 'up' | 'left' | 'right' | 'zoom' | 'fade'

const ENTER: Record<Direction, string> = {
    up: 'slide-in-from-bottom-8',
    left: 'slide-in-from-left-8',
    right: 'slide-in-from-right-8',
    zoom: 'zoom-in-90',
    fade: ''
}

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
    direction?: Direction
    as?: ElementType
    id?: string
}

/** Fades its content in the first time it scrolls into view. */
export default function Reveal({
    children,
    className,
    delay = 0,
    direction = 'up',
    as: Tag = 'div',
    id
}: RevealProps) {
    const ref = useRef<HTMLElement>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const node = ref.current

        if (!node) return

        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true)

            return
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setVisible(true)
                observer.disconnect()
            }
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })

        observer.observe(node)

        return () => observer.disconnect()
    }, [])

    const style: CSSProperties | undefined = delay ? { animationDelay: `${delay}ms` } : undefined

    return (
        <Tag
            ref={ref}
            id={id}
            style={style}
            className={cn(
                visible ? cn('animate-in fade-in fill-mode-both duration-700 ease-out', ENTER[direction]) : 'opacity-0',
                'motion-reduce:animate-none motion-reduce:opacity-100',
                className
            )}
        >
            {children}
        </Tag>
    )
}
